import React, { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, X } from 'lucide-react';
import { useRestaurantStore } from '../../stores/appRestaurantStore';
import { createRestaurantPlatform, fetchRestaurantSectionPlatforms } from '../../services/restaurant/restaurantPlatformService';
import { createRestaurantRevenueSource, fetchRevenueSourcesByRestaurantIds } from '../../services/restaurant/restaurantRevenueSourceService';
import { createRestaurantSection } from '../../services/restaurant/restaurantSectionService';
import { RestaurantSectionType } from '../../types/RestaurantSectionType';
import { Platform } from '../../types/Platforms';
import { RevenueSource } from '../../types/RevenueSource';
import { RevenueSourceType } from '../../types/RevenueSourceType';
import WizardProgressBar from './WizardProgressBar';
import { WizardStep, WizardState, DataSourceType, initialWizardState } from './wizardTypes';
import ChooseTypeStep from './steps/ChooseTypeStep';
import SelectSectionStep from './steps/SelectSectionStep';
import SelectSupplierStep from './steps/SelectSupplierStep';
import ConfigureSupplierStep from './steps/ConfigureSupplierStep';
import ConfigureRevenueStep from './steps/ConfigureRevenueStep';
import EmailForwardingStep from './steps/EmailForwardingStep';
import ConfirmationStep from './steps/ConfirmationStep';

const SUPPLIER_STEPS: WizardStep[] = ['chooseType', 'selectSection', 'selectSupplier', 'configureSupplier', 'emailForwarding', 'confirmation'];
const REVENUE_STEPS: WizardStep[] = ['chooseType', 'configureRevenue', 'emailForwarding', 'confirmation'];

const AddDataSourceWizard: React.FC = () => {
  const { t } = useTranslation();
  const { restaurantId } = useParams<{ restaurantId: string }>();
  const navigate = useNavigate();
  const [state, setState] = useState<WizardState>({ ...initialWizardState });
  const [currentStep, setCurrentStep] = useState<WizardStep>('chooseType');
  const [history, setHistory] = useState<WizardStep[]>([]);
  const [isSaving, setIsSaving] = useState(false);
  const [isCreatingSection, setIsCreatingSection] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const sections = useRestaurantStore((s) => s.restaurantSections)[restaurantId || ''] || [];
  const restaurants = useRestaurantStore((s) => s.restaurants);
  const setRestaurantSectionPlatforms = useRestaurantStore((s) => s.setRestaurantSectionPlatforms);
  const setRestaurantRevenueSources = useRestaurantStore((s) => s.setRestaurantRevenueSources);
  const loadAllData = useRestaurantStore((s) => s.loadAllData);

  const restaurant = restaurants.find((r) => r.id === restaurantId);
  const currentSteps = state.type === 'revenue' ? REVENUE_STEPS : SUPPLIER_STEPS;

  const goTo = useCallback((step: WizardStep) => {
    setHistory((prev) => [...prev, currentStep]);
    setCurrentStep(step);
    setError(null);
  }, [currentStep]);

  const goBack = useCallback(() => {
    if (history.length === 0) {
      navigate('/profile');
      return;
    }
    const prev = history[history.length - 1];
    setHistory((h) => h.slice(0, -1));
    setCurrentStep(prev);
    setError(null);
  }, [history, navigate]);

  const handleClose = () => {
    navigate('/profile');
  };

  // Step handlers
  const handleChooseType = (type: DataSourceType) => {
    setState((s) => ({ ...s, type }));
    if (type === 'supplier') {
      goTo('selectSection');
    } else {
      goTo('configureRevenue');
    }
  };

  const handleSelectExistingSection = (sectionId: string, sectionName: string, sectionType: RestaurantSectionType) => {
    setState((s) => ({ ...s, sectionId, sectionName, sectionType, isNewSection: false }));
    goTo('selectSupplier');
  };

  const handleCreateSection = async (name: string, type: RestaurantSectionType) => {
    if (!restaurantId) return;
    setIsCreatingSection(true);
    try {
      const newSection = await createRestaurantSection(restaurantId, name, type);
      await loadAllData(true);
      setState((s) => ({
        ...s,
        sectionId: newSection.id,
        sectionName: newSection.name,
        sectionType: newSection.restaurantSectionType,
        isNewSection: true,
      }));
      goTo('selectSupplier');
    } catch (err: any) {
      setError(err.message);
    } finally {
      setIsCreatingSection(false);
    }
  };

  const handleSelectSupplier = (platform: Platform) => {
    setState((s) => ({ ...s, platform }));
    goTo('configureSupplier');
  };

  const handleSave = async () => {
    if (!restaurantId) return;
    setIsSaving(true);
    setError(null);
    try {
      if (state.type === 'supplier' && state.sectionId && state.platform) {
        await createRestaurantPlatform({
          restaurantSectionId: state.sectionId,
          platformName: state.platform,
          enabled: true,
          orderEmails: state.orderEmails.length > 0 ? state.orderEmails : undefined,
          orderClientNames: state.orderClientNames.length > 0 ? state.orderClientNames : undefined,
        });
        const updatedPlatforms = await fetchRestaurantSectionPlatforms(state.sectionId);
        setRestaurantSectionPlatforms(state.sectionId, updatedPlatforms);
      } else if (state.type === 'revenue' && state.revenueSourceName && state.revenueSourceType) {
        await createRestaurantRevenueSource({
          restaurantId,
          revenueSourceName: state.revenueSourceName,
          revenueSourceType: state.revenueSourceType,
          restaurantName: state.restaurantNameInPOS,
          relevantSections: state.relevantSections,
        });
        const updatedSources = await fetchRevenueSourcesByRestaurantIds([restaurantId]);
        const forRestaurant = updatedSources.filter((s) => s.restaurantId === restaurantId);
        setRestaurantRevenueSources(restaurantId, forRestaurant);
      }
      navigate('/profile');
    } catch (err: any) {
      setError(err.message);
    } finally {
      setIsSaving(false);
    }
  };

  if (!restaurantId) {
    return <div className="p-8 text-center text-muted dark:text-muted-dark">Restaurant not found</div>;
  }

  return (
    <div className="min-h-screen">
      {/* Header */}
      <div className="border-b border-zinc-200 dark:border-zinc-800 bg-card dark:bg-card-dark">
        <div className="max-w-2xl mx-auto px-6 py-4 flex items-center justify-between">
          <button
            onClick={goBack}
            className="flex items-center gap-1 text-sm text-muted dark:text-muted-dark hover:text-text dark:hover:text-text-dark transition"
          >
            <ArrowLeft className="w-4 h-4" />
            {t('common.back')}
          </button>
          <div className="text-center">
            <h1 className="text-sm font-semibold text-text dark:text-text-dark">{t('wizard.title')}</h1>
            {restaurant && (
              <p className="text-xs text-muted dark:text-muted-dark">{restaurant.name}</p>
            )}
          </div>
          <button
            onClick={handleClose}
            className="p-2 rounded-lg hover:bg-zinc-100 dark:hover:bg-zinc-800 transition"
          >
            <X className="w-5 h-5 text-muted dark:text-muted-dark" />
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-2xl mx-auto px-6 py-8">
        {state.type && (
          <WizardProgressBar steps={currentSteps} currentStep={currentStep} />
        )}

        {currentStep === 'chooseType' && (
          <ChooseTypeStep onSelect={handleChooseType} />
        )}

        {currentStep === 'selectSection' && (
          <SelectSectionStep
            sections={sections}
            onSelectExisting={handleSelectExistingSection}
            onCreateNew={handleCreateSection}
            isCreating={isCreatingSection}
          />
        )}

        {currentStep === 'selectSupplier' && (
          <SelectSupplierStep onSelect={handleSelectSupplier} />
        )}

        {currentStep === 'configureSupplier' && state.platform && (
          <ConfigureSupplierStep
            platform={state.platform}
            orderClientNames={state.orderClientNames}
            orderEmails={state.orderEmails}
            onChangeClientNames={(names) => setState((s) => ({ ...s, orderClientNames: names }))}
            onChangeEmails={(emails) => setState((s) => ({ ...s, orderEmails: emails }))}
            onNext={() => goTo('emailForwarding')}
          />
        )}

        {currentStep === 'configureRevenue' && (
          <ConfigureRevenueStep
            revenueSourceName={state.revenueSourceName}
            revenueSourceType={state.revenueSourceType}
            restaurantNameInPOS={state.restaurantNameInPOS}
            relevantSections={state.relevantSections}
            onChangeSourceName={(name: RevenueSource) => setState((s) => ({ ...s, revenueSourceName: name }))}
            onChangeSourceType={(type: RevenueSourceType) => setState((s) => ({ ...s, revenueSourceType: type }))}
            onChangeRestaurantName={(name: string) => setState((s) => ({ ...s, restaurantNameInPOS: name }))}
            onChangeSections={(sections: string[]) => setState((s) => ({ ...s, relevantSections: sections }))}
            onNext={() => goTo('emailForwarding')}
          />
        )}

        {currentStep === 'emailForwarding' && (
          <EmailForwardingStep onNext={() => goTo('confirmation')} />
        )}

        {currentStep === 'confirmation' && (
          <ConfirmationStep
            state={state}
            onSave={handleSave}
            isSaving={isSaving}
            error={error}
          />
        )}
      </div>
    </div>
  );
};

export default AddDataSourceWizard;
